import type { EffectsState } from '../effects/types'
import type { FullRenderSettings } from '../types'
import type { AsciiBgRuntimeConfig, HtmlJsExportConfig, ReactExportConfig } from './types'
import { extractEnabledEffects, getImageDimensions } from './utils'

type RuntimeExportConfig = Pick<
  HtmlJsExportConfig | ReactExportConfig,
  'adaptivePerf' | 'alphaMask' | 'fadeIn' | 'interaction' | 'pauseOffScreen' | 'transparent'
>

export function createAsciiRuntimeConfig(
  settings: FullRenderSettings,
  effectsState: EffectsState,
  exportConfig: RuntimeExportConfig,
  sourceImage?: HTMLImageElement | string
): AsciiBgRuntimeConfig {
  const { enabledEffects, effectsConfig } = extractEnabledEffects(effectsState)
  const dims = getImageDimensions(sourceImage || '')

  return {
    engine: settings.style,
    animation: settings.animationType,
    animationParams: {
      amplitude: settings.ditherWaveAmplitude,
      frequency: settings.ditherWaveFrequency,
      speed: settings.ditherWaveSpeed,
    },
    effects: enabledEffects,
    effectsConfig,
    transparent: exportConfig.transparent,
    alphaMask: exportConfig.alphaMask,
    pauseOffScreen: exportConfig.pauseOffScreen,
    interaction: exportConfig.interaction || settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled,
    fadeIn: exportConfig.fadeIn,
    adaptivePerf: exportConfig.adaptivePerf,
    targetFPS: 60,

    fontSize: settings.fontSize,
    charSpacing: settings.charSpacing,
    spacing: settings.spacing,
    contrast: settings.contrast,
    brightness: settings.brightness,
    opacity: settings.opacity,
    invert: settings.invert,
    backgroundColor: settings.backgroundColor,
    colorMode: settings.colorMode,
    customColor: settings.customColor,

    charset: settings.charset,
    customCharset: settings.customCharset,
    brailleVariant: settings.brailleVariant,
    terminalCharset: settings.terminalCharset,
    renderFont: settings.renderFont,

    halftoneShape: settings.halftoneShape,
    halftoneSize: settings.halftoneSize,
    halftoneRotation: settings.halftoneRotation,

    ditherType: settings.ditherType,
    ditherStrength: settings.ditherStrength,
    ditherColorNum: settings.ditherColorNum,
    ditherWaveAmplitude: settings.ditherWaveAmplitude,
    ditherWaveFrequency: settings.ditherWaveFrequency,
    ditherWaveSpeed: settings.ditherWaveSpeed,
    ditherWaveColor: settings.ditherWaveColor,
    ditherMouseRadius: settings.ditherMouseRadius,
    ditherMouseInteractionEnabled: settings.ditherMouseInteractionEnabled,

    dither2AnimationEnabled: settings.dither2AnimationEnabled,
    dither2MouseInteractionEnabled: settings.dither2MouseInteractionEnabled,
    dither2MouseRadius: settings.dither2MouseRadius,
    dither2ColorNum: settings.dither2ColorNum,
    dither2PixelSize: settings.dither2PixelSize,
    dither2WaveAmplitude: settings.dither2WaveAmplitude,
    dither2WaveFrequency: settings.dither2WaveFrequency,
    dither2WaveSpeed: settings.dither2WaveSpeed,
    dither2WaveColor: settings.dither2WaveColor,

    retroDuotone: settings.retroDuotone,
    retroNoise: settings.retroNoise,

    lineLength: settings.lineLength,
    lineWidth: settings.lineWidth,
    lineThickness: settings.lineThickness,
    lineRotation: settings.lineRotation,

    particleDensity: settings.particleDensity,
    particleChar: settings.particleChar,
    claudeDensity: settings.claudeDensity,

    imageWidth: dims.width,
    imageHeight: dims.height,

    mouseInteractionMode: settings.mouseInteractionMode,
    hoverStrength: settings.hoverStrength,
    mouseAreaSize: settings.mouseAreaSize,
    mouseSpread: settings.mouseSpread,

    animationEnabled: settings.ditherAnimationEnabled || settings.dither2AnimationEnabled,
    mouseInteractionEnabled: settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled,
    mouseInfluenceRadius: settings.mouseAreaSize,
    mouseInfluenceStrength: settings.mouseSpread,
  }
}
